import React, { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { PanelRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import type { DetailEditMode } from "@/lib/store/preferencesStore";
import type { DocFieldSchema } from "@/lib/erp/meta";
import type { FieldSaveState } from "@/components/inline-edit/detail/types";
import { InlineFieldRenderer } from "@/components/inline-edit/detail/InlineFieldRenderer";
import { INLINE_DIRTY_PULSE_CLASS, INLINE_ERROR_RING_CLASS } from "@/components/inline-edit/detail/inline-field-styles";
import { formatShopifyFieldValue } from "./utils/shopifyFormatting";
import { renderFormattedValue } from "./utils/renderFormattedValue";
import { FieldInfoTooltip } from "./FieldInfoTooltip";

/** Field types that always fall back to modal */
const MODAL_ONLY_TYPES = new Set(["Table", "Table MultiSelect"]);

export interface InlineFieldRowProps {
  field: DocFieldSchema;
  value: unknown;
  label: string;
  editMode: DetailEditMode;
  isEditing: boolean;
  onStartEdit: () => void;
  onCommit: (value: unknown) => void;
  onCancel: () => void;
  isReadOnly?: boolean;
  saveState?: FieldSaveState;
  currency?: string;
  placeholder?: string;
  tooltip?: string;
  fullWidth?: boolean;
  onModalClick?: () => void;
  /** Called on Tab/Shift+Tab to advance to next/prev field */
  onAdvance?: (direction: "next" | "prev") => void;
  /** Called to retry a failed save */
  onRetry?: () => void;
  // Density classes — widget passes per row
  rowPx: string;
  rowPy: string;
  rowGap: string;
  rowLabel: string;
  dividerMx: string;
  isLast?: boolean;
  isRequired?: boolean;
  /** Server validation error message for this field (Plan 166 Epic 02) */
  error?: string;
  /** Clear this field's error when user starts editing */
  onClearError?: () => void;
  /** Custom content for Table fields (e.g., ChildTablePreview) */
  children?: React.ReactNode;
}

/**
 * InlineFieldRow — dual-mode field row for iPad Settings layout.
 *
 * In modal mode: renders label + formatted value (read-only). Parent card handles click → modal.
 * In inline mode: renders label + InlineFieldRenderer (editable). Table fields show modal icon.
 */
export function InlineFieldRow({
  field,
  value,
  label,
  editMode,
  isEditing,
  onStartEdit,
  onCommit,
  onCancel,
  isReadOnly,
  saveState,
  currency,
  placeholder,
  tooltip,
  fullWidth,
  onModalClick,
  onAdvance,
  onRetry,
  rowPx,
  rowPy,
  rowGap,
  rowLabel,
  dividerMx,
  isLast,
  isRequired,
  error,
  onClearError,
  children,
}: InlineFieldRowProps) {
  const isTable = MODAL_ONLY_TYPES.has(field.fieldtype);
  const isCheck = field.fieldtype === "Check";

  // --- Dirty pulse: brief ring when buffer commit lands (no checkmark since nothing persisted) ---
  const [showPulse, setShowPulse] = useState(false);
  const prevValueRef = useRef(value);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      prevValueRef.current = value;
      return;
    }
    if (value !== prevValueRef.current && editMode === "inline" && saveState?.status === "idle") {
      setShowPulse(true);
    }
    prevValueRef.current = value;
  }, [value, editMode, saveState?.status]);

  useEffect(() => {
    if (!showPulse) return;
    const t = setTimeout(() => setShowPulse(false), 150);
    return () => clearTimeout(t);
  }, [showPulse]);

  // --- Modal mode: existing display layout ---
  if (editMode === "modal") {
    let displayValue: React.ReactNode;
    if (isTable) {
      displayValue = children;
    } else if (isCheck) {
      const isChecked = value === 1 || value === true;
      displayValue = <Switch checked={isChecked} disabled className="pointer-events-none" />;
    } else {
      const formatted = formatShopifyFieldValue(field, value, currency);
      displayValue = (
        <span className="break-words whitespace-pre-wrap">
          {renderFormattedValue(formatted)}
        </span>
      );
    }

    return (
      <>
        <div
          className={cn(
            fullWidth || isTable ? "flex flex-col gap-2" : `flex items-center ${rowGap}`,
            rowPx,
            rowPy,
          )}
        >
          <span
            className={cn(rowLabel, "shrink-0", fullWidth || isTable ? "w-full" : "")}
            style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
          >
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </span>
          <div
            className={cn(
              "text-foreground",
              fullWidth || isTable
                ? `w-full ${rowLabel}`
                : `${rowLabel} flex-1 min-w-0 text-right flex items-center justify-end gap-2`,
            )}
          >
            {displayValue}
            {!isTable && !isCheck && !fullWidth && tooltip && (
              <FieldInfoTooltip description={tooltip} label={label} />
            )}
          </div>
        </div>
        {!isLast && <div className={cn(dividerMx, "border-t border-border/40")} />}
      </>
    );
  }

  // --- Inline mode ---

  // Table/TableMultiSelect: show display value + modal icon
  if (isTable) {
    return (
      <>
        <div
          className={cn("flex flex-col gap-2 cursor-pointer", rowPx, rowPy)}
          onClick={(e) => {
            e.stopPropagation();
            onModalClick?.();
          }}
        >
          <div className="flex items-center justify-between">
            <span
              className={cn(rowLabel, "shrink-0")}
              style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
            >
              {label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </span>
            <PanelRight className="h-4 w-4 text-muted-foreground/50 shrink-0" />
          </div>
          <div className="w-full text-foreground">{children}</div>
        </div>
        {!isLast && <div className={cn(dividerMx, "border-t border-border/40")} />}
      </>
    );
  }

  // Regular fields: InlineFieldRenderer handles display/edit/readonly/check
  // Whole row is clickable to activate edit (unless readonly, check, or already editing)
  const isRowClickable = !isReadOnly && !isCheck && !isEditing;
  const handleRowClick = () => {
    if (isRowClickable) {
      onClearError?.();
      onStartEdit();
    }
  };
  const errorId = error ? `${field.fieldname}-error` : undefined;
  return (
    <>
      <div
        className={cn(
          fullWidth ? "flex flex-col gap-2" : `flex items-center ${rowGap}`,
          rowPx,
          rowPy,
          isRowClickable && "cursor-text",
          showPulse && INLINE_DIRTY_PULSE_CLASS,
          error && `${INLINE_ERROR_RING_CLASS} bg-destructive/5`,
          "transition-shadow duration-150",
        )}
        data-inline-field={field.fieldname}
        onClick={isRowClickable ? handleRowClick : undefined}
        aria-invalid={!!error || undefined}
        aria-describedby={errorId}
      >
        <span
          className={cn(rowLabel, "shrink-0", fullWidth ? "w-full" : "")}
          style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
        >
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
          {!fullWidth && tooltip && (
            <span className="ml-1 inline-flex">
              <FieldInfoTooltip description={tooltip} label={label} />
            </span>
          )}
        </span>
        <div
          className={cn(
            "text-foreground",
            fullWidth ? `w-full ${rowLabel}` : `${rowLabel} flex-1 min-w-0 text-right flex items-center justify-end`,
          )}
        >
          <InlineFieldRenderer
            field={field}
            value={value}
            isEditing={isEditing}
            onStartEdit={onStartEdit}
            onCommit={onCommit}
            onCancel={onCancel}
            isReadOnly={isReadOnly}
            saveState={saveState}
            placeholder={placeholder}
            currency={currency}
            onAdvance={onAdvance}
            onRetry={onRetry}
          />
        </div>
      </div>
      {error && (
        <p id={errorId} className={cn("text-destructive text-xs", rowPx, "pb-1")}>
          {error}
        </p>
      )}
      {!isLast && <div className={cn(dividerMx, "border-t border-border/40")} />}
    </>
  );
}
